<template>
    <div
        class="cz2__two_pack_selector--wrapper"
        role="region"
        aria-label="Building your Custom Pop"
    >
        <!-- SPELLING BEE START: Borders and logos -->
        <template v-if="isSpellingBee">
            <div class="cz2__sb-border" />
            <div class="cz2__sb-background" />

            <div
                class="cz2__sb-logo-m-two-pack"
                @click.prevent="tapLogo"
            />

            <div
                class="cz2__sb-logo-d-two-pack"
                @click.prevent="tapLogo"
            />

            <div
                class="cz2__sb-logo-k-two-pack"
                @click.prevent="tapLogo"
            />
        </template>
        <!-- SPELLING BEE END -->

        <splash-header
            :has-video="splashVideoUrl !== null"
            :extra-padding="!showCalendarCard && isKiosk"
        />

        <div class="cz2__two_pack_selector__content-wrapper">
            <ul
                :class="[{
                    'is-mobile': isMobile,
                    'overflow-x-auto': overflowAuto,
                    'extra-padding': !showCalendarCard && isKiosk,
                }]"
                class="cz2__two_pack_selector cz2__two_pack_selector__content"
            >
                <li
                    ref="onePackButton"
                    class="cz2__two_pack_selector__button-li"
                >
                    <two-pack-selector-card
                        @click.native="triggerStart('one-pack')"
                        :pack-image="onePackImage"
                        :banner-color="onePackBannerColor"
                        :banner-copy="$t('PACK_CARD_FLAG_COPY')"
                        :heading-copy="$t('PACK_CARD_HEADING')"
                        :content-copy="$t('PACK_CARD_COPY')"
                        :price-copy="getPriceCopy(onePackPrice)"
                        :cta-copy="$t('BUILD_POP')"
                    />
                </li>

                <li
                    ref="twoPackButton"
                    class="cz2__two_pack_selector__button-li"
                >
                    <two-pack-selector-card
                        @click.native="triggerStart('two-pack')"
                        :pack-image="twoPackImage"
                        :banner-color="twoPackBannerColor"
                        :banner-copy="$t('2_PACK_CARD_FLAG_COPY')"
                        :heading-copy="$t('2_PACK_CARD_HEADING')"
                        :content-copy="$t('2_PACK_CARD_COPY')"
                        :price-copy="getPriceCopy(twoPackPrice)"
                        :cta-copy="$t('BUILD_2_PACK')"
                    />
                </li>

                <li
                    v-if="popPeopleUrl && !isKiosk"
                    ref="popPeopleButton"
                    class="cz2__two_pack_selector__button-li"
                >
                    <two-pack-selector-card
                        @click.native="openPopPeopleUrl"
                        :pack-image="popPeopleImage"
                        :banner-copy="$t('POP_PEOPLE_CARD_FLAG')"
                        :banner-color="popPeopleBannerColor"
                        :heading-copy="$t('POP_PEOPLE_CARD_HEADING')"
                        :content-copy="$t('POP_PEOPLE_CARD_COPY')"
                        :price-copy="getPopPeoplePriceCopy(popPeoplePrice)"
                        :cta-copy="$t('POP_PEOPLE_CARD_CTA')"
                    />
                </li>

                <li
                    v-if="showCalendarCard && (isKiosk || isMobile)"
                    ref="calendarCard"
                    class="cz2__two_pack_selector__button-li"
                >
                    <two-pack-selector-card
                        @click.native="addCalendar"
                        :pack-image="calendarImage"
                        :banner-copy="$t('CALENDAR_CARD_FLAG')"
                        :banner-color="calendarBannerColor"
                        :heading-copy="$t('CALENDAR_CARD_HEADING')"
                        :content-copy="$t('CALENDAR_CARD_COPY')"
                        :price-copy="getCalendarPriceCopy(calendarPrice)"
                        :cta-copy="$t('CALENDAR_CARD_CTA')"
                    />
                </li>
            </ul>

            <div
                v-if="showCalendarCard && !isKiosk && !isMobile"
                class="cz2__calendar-button-wrapper cz2__two_pack_selector__button-li"
            >
                <two-pack-selector-card
                    @click.native="addCalendar"
                    :pack-image="calendarImage"
                    :banner-copy="$t('CALENDAR_CARD_FLAG')"
                    :banner-color="calendarBannerColor"
                    :heading-copy="$t('CALENDAR_CARD_HEADING')"
                    :content-copy="$t('CALENDAR_CARD_COPY')"
                    :price-copy="getCalendarPriceCopy(calendarPrice)"
                    :cta-copy="$t('CALENDAR_CARD_CTA')"
                    :is-calendar="true"
                />
            </div>
        </div>

        <banner-ad :extra-padding="!showCalendarCard && isKiosk" />

        <splash-video
            v-if="!isKiosk"
            :splash-video-url="splashVideoUrl"
        />

        <!-- <img
            src="../assets/images/toty-logo.png"
            :alt="$t('LOGO_TOTY')"
            class="cz2__toty-logo"
        > -->
    </div>
</template>

<script>
    import { animate } from 'motion';

    import { formatPrice } from '../helpers/formatPrice';

    import SplashHeader from './SplashHeader.vue';
    import MainNavigationListButton from './MainNavigationListButton.vue';
    import RedBannerButton from './RedBannerButton.vue';
    import Btn from './ui/Btn.vue';
    import SbButton from './SbButton.vue';
    import SbBannerButton from './SbBannerButton.vue';
    import TwoPackSelectorCard from './TwoPackSelectorCard.vue';
    import BannerAd from './BannerAd.vue';
    import SplashVideo from './SplashVideo.vue';

    export default {
        name: 'TwoPackSelector',

        components: {
            SplashHeader,
            Btn,
            MainNavigationListButton,
            SbBannerButton,
            RedBannerButton,
            SbButton,
            TwoPackSelectorCard,
            BannerAd,
            SplashVideo,
        },

        data() {
            return {
                overflowAuto: false,
            };
        },

        computed: {
            globalSettings() {
                return this.$store.state.customizer.globalSettings?.blueprint?.custom;
            },

            globalSettingsController() {
                return this.$store.state.customizer.globalSettings;
            },

            onePackImage() {
                const newImage = this.globalSettings?.['fun-splash-one-pack-image-new'];
                const oldImage = this.globalSettings?.['fun-splash-one-pack-image'];

                return `${newImage || oldImage}?fmt=webp&qlt=70`;
            },

            twoPackImage() {
                const newImage = this.globalSettings?.['fun-splash-two-pack-image-new'];
                const oldImage = this.globalSettings?.['fun-splash-two-pack-image'];

                return `${newImage || oldImage}?fmt=webp&qlt=70`;
            },

            calendarImage() {
                const newImage = this.globalSettings?.['fun-splash-holiday-calendar-image'];

                return `${newImage}?fmt=webp&qlt=70`;
            },

            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            isMobile() {
                return this.$store.state.customizer.isMobile;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            onePackPrice() {
                // Fallback to override.
                if (this.globalSettings?.['fun-one-pack-price']?.length > 0) {
                    return this.globalSettings?.['fun-one-pack-price'];
                }

                const sku = this.globalSettings?.['fun-one-pack-price-sku'];
                if (sku?.length > 0) {
                    const price = this.getPrice(sku);
                    if (price != null) {
                        return price;
                    }
                }

                return '';
            },

            twoPackPrice() {
                // Fallback to override.
                if (this.globalSettings?.['fun-two-pack-price']?.length > 0) {
                    return this.globalSettings?.['fun-two-pack-price'];
                }

                const sku = this.globalSettings?.['fun-two-pack-price-sku'];
                if (sku?.length > 0) {
                    const price = this.getPrice(sku);
                    if (price != null) {
                        return price;
                    }
                }

                return '';
            },

            popPeoplePrice() {
                return Number(this.globalSettings?.['fun-splash-pop-people-price']);
            },

            calendarPrice() {
                const settingsController = this.globalSettingsController;

                const stockCheck = settingsController.findPlacement('POPHolidayCalendar');

                if (stockCheck == null) {
                    return null;
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const sku of stockCheck.components) {
                    if (sku.placeholder) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (settingsController.isAvailable(sku)) {
                        const price = this.getPrice(sku.custom['fun-sku']);

                        if (price != null) {
                            return price;
                        }
                    }
                }

                return null;
            },

            splashVideoUrl() {
                const url = this.globalSettings?.['fun-splash-video'];

                if (!url || url === 'none') {
                    return null;
                }

                return url;
            },

            showCalendarCard() {
                if (this.globalSettings?.['fun-splash-holiday-calendar-enabled'] !== 'true') {
                    return false;
                }

                const settingsController = this.globalSettingsController;

                const stockCheck = settingsController.findPlacement('POPHolidayCalendar');

                if (stockCheck == null) {
                    return false;
                }

                // eslint-disable-next-line no-restricted-syntax
                for (const sku of stockCheck.components) {
                    if (sku.placeholder) {
                        // eslint-disable-next-line no-continue
                        continue;
                    }

                    if (settingsController.isAvailable(sku)) {
                        return true;
                    }
                }

                return false;
            },

            popPeopleUrl() {
                return this.globalSettings?.['fun-splash-pop-people-url'];
            },

            popPeopleImage() {
                return this.globalSettings?.['fun-splash-pop-people-image'];
            },

            onePackBannerColor() {
                return this.globalSettings?.['fun-splash-one-pack-banner-color'];
            },

            twoPackBannerColor() {
                return this.globalSettings?.['fun-splash-two-pack-banner-color'];
            },

            popPeopleBannerColor() {
                return this.globalSettings?.['fun-splash-pop-people-banner-color'];
            },

            calendarBannerColor() {
                return this.globalSettings?.['fun-splash-calendar-banner-color'];
            },
        },

        mounted() {
            this.animateButtons();
        },

        methods: {
            getPrice(sku) {
                // eslint-disable-next-line prefer-destructuring
                const state = this.$store.state;

                if (state.ecommerce.products && state.ecommerce.products[sku]) {
                    const product = state.ecommerce.products[sku];

                    return product.price;
                }

                if (state.options.alwaysAvailable) {
                    return 10;
                }

                return null;
            },

            animateButtons() {
                const {
                    onePackButton,
                    twoPackButton,
                    popPeopleButton,
                    calendarCard,
                } = this.$refs;
                const webElements = this.popPeopleUrl ? [onePackButton, twoPackButton, popPeopleButton] : [onePackButton, twoPackButton];
                const kioskElements = this.showCalendarCard ? [onePackButton, twoPackButton, calendarCard] : [onePackButton, twoPackButton];
                const elements = this.isKiosk ? kioskElements : webElements;

                elements.forEach((button) => {
                    animate(button, {
                        transform: [
                            'scale(0)',
                            'scale(0.6)',
                            'scale(1)',
                            'scale(1.05)',
                            'scale(1)',
                            'scale(0.98)',
                            'scale(1)',
                        ],
                    }, {
                        duration: 1,
                        easing: 'ease-in-out',
                    });
                });

                // Prevent scrollbar from temporarily showing during transition.
                setTimeout(() => {
                    this.overflowAuto = true;
                }, 1000);
            },

            tapLogo() {
                this.$store.dispatch('tapLogo');
            },

            showAiModal(packType) {
                if (this.isKiosk) {
                    this.$emit('start', packType);
                } else if (this.globalSettings['fun-ai-selfie'] === 'true') {
                    // Store pack type so the AI modal can load the right experience.
                    this.$store.dispatch('setAiTrigger', true);

                    this.$emit('start', packType);
                } else {
                    this.$emit('start', packType);
                }
            },

            triggerStart(packType) {
                this.$emit('start', packType);
            },

            getPriceCopy(price) {
                const formattedPrice = formatPrice(this.$store.state, price, 'standard');

                return this.$t('PACK_CARD_PRICE').replace('{0}', `<b class="text-black">${formattedPrice}</b>`);
            },

            getCalendarPriceCopy(price) {
                const formattedPrice = formatPrice(this.$store.state, price, 'standard');

                return this.$t('CALENDAR_CARD_PRICE').replace('{0}', `<b class="text-black">${formattedPrice}</b>`);
            },

            getPopPeoplePriceCopy(price) {
                const formattedPrice = formatPrice(this.$store.state, price, 'standard');

                return this.$t('POP_PEOPLE_CARD_PRICE').replace('{0}', `<b class="text-black">${formattedPrice}</b>`);
            },

            emitStart(packType) {
                this.$emit('start', packType);
            },

            addCalendar() {
                this.$store.dispatch('setAutoAddHolidayCalendar', true);

                this.triggerStart('one-pack');
            },

            openPopPeopleUrl() {
                window.open(this.popPeopleUrl, '_blank');
            },
        },
    };
</script>

<style lang="scss">
    @import '../style/variables';

    #cz2.cz2 {
        .cz2__sb-logo-m-two-pack, .cz2__sb-logo-d-two-pack, .cz2__sb-logo-k-two-pack {
            display: none;
        }

        .cz2__two_pack_selector--wrapper {
            position: absolute;

            top: 0;
            left: 0;
            right: 0;
            bottom: 0;

            width: 100%;

            display: flex;
            flex-direction: column;
            align-items: center;

            background-color: white;
        }

        .cz2__two_pack_selector {
            margin-top: 0;
            padding: 0 32px;
            display: flex;
            align-items: stretch;
            background: rgba(0, 0, 0, 0);

            .cz2__toty-logo {
                top: 4px;
                right: 4px;
            }
        }

        .cz2__two_pack_selector__content-wrapper {
            width: 100%;
            background-color: #F5F5F5;
            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .cz2__two_pack_selector__content {
            width: 100%;
            display: flex;
            gap: 16px;
            padding: 24px 16px;
            overflow-x: hidden;

            &.overflow-x-auto {
                overflow-x: auto;
            }

            @include respond-above(lg) {
                max-width: 968px;
                gap: 32px;
                align-items: center;

                padding: 32px 0;
            }
        }

        .cz2__two_pack_selector__button-li {
            flex: 1;
        }

        .cz2__calendar-button-wrapper {
            margin-bottom: 32px;

            width: 100%;
            background-color: #F5F5F5;

            display: flex;
            flex-direction: row;

            .cz2__two_pack_selector__button {
                flex-direction: row;
            }

            .cz2__two_pack_selector__button-content {
                order: -1;
                flex: 1;
            }

            .cz2__two_pack_selector__content__image-container {
                width: 45%;
                height: 100%;
            }

            .cz2__btn--secondary {
                width: auto;
                align-self: start;
            }
        }

        @include respond-above(lg) {
            .cz2__two_pack_selector__button-li {
                padding: 0;
            }

            .cz2__calendar-button-wrapper {
                max-width: 968px;
            }
        }

        @include respond-above(md) {
            .cz2__two_pack_selector__button-li {
                height: 100%;
            }
        }

        .text-black {
            color: black;
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__two_pack_selector--wrapper {
            overflow: hidden;
            height: 100%;
        }

        .cz2__two_pack_selector__content-wrapper {
            flex: 1;
        }

        .cz2__two_pack_selector__content {
            flex: 1;
            flex-direction: column;
            gap: 16px;
            max-width: 800px;

            &.extra-padding {
                padding: 64px 0;
                gap: 32px;
            }
        }

        .cz2__two_pack_selector__button-li {
            flex: 1;
            width: 100%;
        }

        .cz2__two_pack_selector__button {
            height: 100%;
            aspect-ratio: initial;
        }

        .cz2__two_pack_selector {
            .cz2__toty-logo {
                top: 4px;
                left: 4px;
                width: 239px;
                height: 239px;
            }
        }
    }
</style>
